import React, { ReactNode } from 'react';
import { Box, Typography, styled, Stack, IconButton } from '@mui/material';
import { AutoAwesome, Close } from '@mui/icons-material';
import {
  BORDER_COLOR,
  BANNER_BACKGROUND,
  GRADIENT_PRIMARY,
  GRADIENT_TEXT,
} from 'components/AI/AIStyles';
import { FeatureItem } from './components';

const BannerContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(4),
  background: BANNER_BACKGROUND,
  border: '1px solid',
  borderColor: BORDER_COLOR,
  borderRadius: theme.shape.borderRadius,
  flex: 3,
  position: 'relative',
}));

const IconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
  background: GRADIENT_PRIMARY,
  borderRadius: '50%',
  color: theme.palette.common.white,
}));

export const GradientTypography = styled(Typography)({
  backgroundImage: GRADIENT_TEXT,
  backgroundClip: 'text',
  color: 'transparent',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
});

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

export interface AIBannerBaseProps {
  /**
   * Title to display in the banner
   */
  title: string;
  /**
   * Description text for the banner
   */
  description: string;
  /**
   * Custom icon to display instead of the default sparkles icon
   */
  customIcon?: ReactNode;
  /**
   * Array of features to display
   */
  features?: Array<{
    id: string;
    icon: ReactNode;
    label: string;
    iconColor?: string;
  }>;
  /**
   * Whether the banner can be dismissed
   */
  dismissible?: boolean;
  /**
   * Callback function when the banner is dismissed
   */
  onDismiss?: () => void;
}

/**
 * AIBannerBase component to display information about AI features
 *
 * @param props - Component props
 * @returns React component
 */
export const AIBannerBase: React.FC<AIBannerBaseProps> = ({
  title,
  description,
  customIcon,
  features = [],
  dismissible = false,
  onDismiss,
}) => (
  <BannerContainer>
    {dismissible && onDismiss && (
      <CloseButton
        onClick={onDismiss}
        size="large"
        aria-label="Dismiss banner"
        data-testid="dismiss-button"
      >
        <Close fontSize="small" />
      </CloseButton>
    )}
    <HeaderContainer>
      <IconContainer data-testid="icon-container">
        {customIcon || <AutoAwesome fontSize="large" />}
      </IconContainer>
      <Box>
        <GradientTypography variant="h4" fontWeight="bold">
          {title}
        </GradientTypography>
        <Typography variant="body1" sx={{ mt: 1 }}>
          {description}
        </Typography>
      </Box>
    </HeaderContainer>
    <Stack direction="row" alignItems="center" spacing={6} mt={2}>
      {features.map((feature) => (
        <FeatureItem
          key={feature.id}
          icon={feature.icon}
          label={feature.label}
          iconColor={feature.iconColor}
        />
      ))}
    </Stack>
  </BannerContainer>
);

export default AIBannerBase;
